// src/context/AuthContext.js
/**
 * Global authentication context.
 *
 * Exposes:
 *   • `user`    Firebase user object (or `null` when signed-out).
 *   • `loading` Flag while auth state is being resolved on boot.
 *
 * DI-0093: Prolonged sign-in with secure session handling
 * ------------------------------------------------------
 * We configure Firebase Auth persistence *before* subscribing to auth state.
 * This allows us to enforce a consent-driven policy:
 *
 *   • Consent unknown (default) -> do NOT persist across browser restarts.
 *   • Essential storage accepted -> persist across browser restarts.
 *
 * Security posture
 * ----------------
 * • We do not store tokens ourselves (no localStorage/cookie token handling).
 * • We rely on Firebase Auth persistence modes and refresh handling.
 * • We "fail closed" (non-persistent) if consent is unknown or persistence
 *   configuration fails.
 *
 * Implementation notes:
 *   1. `onIdTokenChanged` fires once on mount, whenever the user's
 *      authentication state changes, and when Firebase refreshes the session
 *      token/custom claims in place.
 *   2. The user object is set as soon as Firebase returns it.
 *   3. Backend-derived account/profile bootstrap now happens in
 *      `RegionalBackendContext`, which keeps profile reads out of the browser's
 *      direct Firebase Storage path.
 *   4. `contextValue` is memoised to prevent needless re-renders.
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  getAuth,
  onIdTokenChanged,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  inMemoryPersistence,
} from "firebase/auth";
import { firebaseApp } from "../firebase/firebaseApp";
import { useConsent } from "./ConsentContext"; // <-- add this context

// Provide sensible defaults to avoid undefined checks in consumers.
const AuthContext = createContext({
  user: null,
  loading: true,
  authVersion: 0,
});

/**
 * Derive an Auth persistence mode from cookie/storage consent.
 *
 * Policy:
 * - Unknown consent -> session-only (safe default; survives reloads, not restarts).
 * - Essential accepted -> local persistence (survives browser restarts).
 * - Essential explicitly declined -> in-memory (most strict; clears on refresh).
 *
 * @param {{ status: "unknown" | "set", essential?: boolean }} consent
 * @returns {import("firebase/auth").Persistence}
 */
function getPersistenceForConsent(consent) {
  if (!consent || consent.status !== "set") return browserSessionPersistence;
  if (consent.essential) return browserLocalPersistence;
  return inMemoryPersistence;
}

/**
 * React provider that supplies `user` and `loading` to its subtree.
 */
export function AuthProvider({ children }) {
  // Firebase SDK factories are idempotent; they return singletons.
  const auth = getAuth(firebaseApp);

  // Consent is managed separately (also sets you up for analytics gating later).
  const consent = useConsent();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authVersion, setAuthVersion] = useState(0);

  /* ------------------------------------------------------------------ */
  /* Configure persistence + subscribe to auth state.                    */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    let unsubscribe = null;
    let cancelled = false;

    (async () => {
      // Default: safe / non-persistent until consent is explicitly set.
      const desiredPersistence = getPersistenceForConsent(consent);

      try {
        // Must be configured before attaching onIdTokenChanged.
        await setPersistence(auth, desiredPersistence);
      } catch (err) {
        // Fail closed: use a safe non-persistent behaviour if config fails.
        if (process.env.NODE_ENV === "development") {
          console.error("Auth persistence configuration failed:", err);
        }
      }

      if (cancelled) return;

      unsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
        if (!firebaseUser) {
          setUser(null);
          setAuthVersion((currentValue) => currentValue + 1);
          setLoading(false);
          return;
        }

        if (typeof firebaseUser.getIdToken === "function") {
          try {
            // Prime the ID token once so regional backend bootstrap does not
            // race ahead of Firebase session readiness during sign-in.
            await firebaseUser.getIdToken();
          } catch (tokenError) {
            if (process.env.NODE_ENV === "development") {
              console.error("Firebase ID token bootstrap failed:", tokenError);
            }
          }
        }

        setUser(firebaseUser);
        setAuthVersion((currentValue) => currentValue + 1);
        setLoading(false);
      });
    })();

    return () => {
      cancelled = true;
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, [auth, consent?.status, consent?.essential, consent]);

  /* ------------------------------------------------------------------ */
  /* Memoise context to keep reference stable between renders.           */
  /* ------------------------------------------------------------------ */
  const contextValue = useMemo(
    () => ({ user, loading, authVersion }),
    [authVersion, loading, user],
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {/* Delay rendering children until first auth resolution completes. */}
      {!loading && children}
    </AuthContext.Provider>
  );
}

/**
 * Convenience hook for consuming the authentication context.
 *
 * @returns {{ user: import('firebase/auth').User | null, loading: boolean }}
 */
export const useAuth = () => useContext(AuthContext);
